import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";
import { formatReportDate } from "../../utils/reportDate";
import "./LegacyReports.css";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-IN");
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return formatReportDate(value);
  }
  return String(value);
};

const pendingOptions = [
  [1, "Purchase Order Pending"], [2, "Sales Order Pending"], [3, "Advance Pending"],
  [4, "Shipment Schedule Pending"], [5, "Document Received Pending"], [6, "Final Payment Pending"]
];
const accountTitles = { "invoice-list": "Invoice", "inward-remittance": "Payment Received List", "credit-debit": "Dr/Cr List", "forex-diff": "Forex Diff" };
const AdditionalReports = ({ pending = false }) => {
  const { reportKey } = useParams();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const title = pending ? "Pending Status Report" : accountTitles[reportKey] || "Additional Reports";
  const { suppliers, buyers } = useLookup();
  const [catalog, setCatalog] = useState([]);
  const [type, setType] = useState("");
  const [filters, setFilters] = useState({ shipperId: "", buyerId: "", dealId: "", fromDate: "", toDate: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get("/report/additional/catalog").then((res) => {
      setCatalog(res.data);
      setType(reportKey || res.data[0]?.key || "");
    }).catch((err) => setError(err.response?.data?.message || "Unable to load report catalog."));
  }, [reportKey]);

  useEffect(() => { setReport(null); setError(""); }, [reportKey, pending]);

  const groups = useMemo(() => catalog.reduce((result, item) => {
    result[item.category] = [...(result[item.category] || []), item];
    return result;
  }, {}), [catalog]);

  const generate = async () => {
    if (pending && !selectedStatuses.length) { setError("Select at least one pending report."); return; }
    if (!pending && !type) return;
    try {
      setLoading(true); setError("");
      const payload = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));
      if (payload.fromDate && payload.toDate && payload.fromDate > payload.toDate) { setReport(null); setError("From date cannot be after to date."); return; }
      const res = await axios.post(pending ? "/report/pending-status" : `/report/additional/${type}`, pending ? { ...payload, statuses: selectedStatuses } : payload);
      setReport(res.data);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || "Failed to generate report.");
    } finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!report) return;
    const data = [
      report.columns.map((column) => column.label),
      ...report.rows.map((row) => report.columns.map((column) => row[column.key]))
    ];
    const csv = data.map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${report.report.key}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const company = report?.company || {};

  return <div className="container-fluid mt-3">
    <div className="card shadow-sm border-0 no-print">
      <div className="card-header bg-white"><span className="badge bg-success fs-6 px-3 py-2">{title}</span></div>
      <div className="card-body"><div className="border rounded p-3 bg-light report-filter">
        <div className="row g-2 mb-2">
          {pending ? <div className="col-md-4 position-relative">
            <label id="pending-reports-label" className="form-label">Pending reports</label>
            <button type="button" className="form-select form-select-sm text-start" aria-labelledby="pending-reports-label" aria-expanded={pickerOpen} aria-controls="pending-report-options" onClick={() => setPickerOpen(!pickerOpen)} onKeyDown={e => { if (e.key === "Escape") setPickerOpen(false); }} disabled={loading}>
              {selectedStatuses.length ? selectedStatuses.length + " reports selected" : "Select pending reports"}
            </button>
            {pickerOpen && <div id="pending-report-options" className="border rounded bg-white p-2 position-absolute shadow" style={{ zIndex: 10 }}>
              <div className="d-flex gap-2 mb-2"><button type="button" className="btn btn-link btn-sm" onClick={() => { setSelectedStatuses(pendingOptions.map(([id]) => id)); setReport(null); }}>Select all</button><button type="button" className="btn btn-link btn-sm" onClick={() => { setSelectedStatuses([]); setReport(null); }}>Clear</button><button type="button" className="btn btn-link btn-sm" onClick={() => setPickerOpen(false)}>Done</button></div>
              {pendingOptions.map(([id, label]) => <label key={id} className="d-block py-1"><input type="checkbox" className="form-check-input me-2" checked={selectedStatuses.includes(id)} onChange={e => { setSelectedStatuses(prev => e.target.checked ? [...prev, id] : prev.filter(x => x !== id)); setReport(null); }} />{label}</label>)}
            </div>}
            <small className="text-muted">{pendingOptions.filter(([id]) => selectedStatuses.includes(id)).map(([, label]) => label).join(", ")}</small>
          </div> : !reportKey && <div className="col-md-4"><label className="form-label">Report</label><select className="form-select form-select-sm" value={type} onChange={e => { setType(e.target.value); setReport(null); }}>
            {Object.entries(groups).map(([category, items]) => <optgroup key={category} label={category}>{items.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}</optgroup>)}
          </select></div>}
          <div className="col-md-3"><label className="form-label">Supplier</label><select className="form-select form-select-sm" value={filters.shipperId} onChange={e => setFilters({ ...filters, shipperId: e.target.value })}><option value="">All Supplier</option>{suppliers.map(x => <option key={x.ShipperID} value={x.ShipperID}>{x.ShipperName}</option>)}</select></div>
          <div className="col-md-3"><label className="form-label">Buyer</label><select className="form-select form-select-sm" value={filters.buyerId} onChange={e => setFilters({ ...filters, buyerId: e.target.value })}><option value="">All Buyer</option>{buyers.map(x => <option key={x.BuyerID} value={x.BuyerID}>{x.BuyerName}</option>)}</select></div>
          <div className="col-md-2"><label className="form-label">Deal ID</label><input type="number" className="form-control form-control-sm" value={filters.dealId} onChange={e => setFilters({ ...filters, dealId: e.target.value })} /></div>
          <div className="col-md-2"><label className="form-label">From Date</label><input type="date" className="form-control form-control-sm" value={filters.fromDate} onChange={e => setFilters({ ...filters, fromDate: e.target.value })} /></div>
          <div className="col-md-2"><label className="form-label">To Date</label><input type="date" className="form-control form-control-sm" value={filters.toDate} onChange={e => setFilters({ ...filters, toDate: e.target.value })} /></div>
        </div>
        {reportKey === "forex-diff" && <div className="alert alert-info">Forex Diff needs the invoice and receipt exchange rate comparison to be defined before this report can be generated.</div>}
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <div className="d-flex gap-2"><button className="btn btn-success btn-sm px-4" onClick={generate} disabled={loading || (pending && !selectedStatuses.length) || reportKey === "forex-diff"}>{loading ? "Generating..." : "Generate Report"}</button>
          {report && <><button className="btn btn-outline-secondary btn-sm" onClick={() => window.print()}>Print / Save PDF</button><button className="btn btn-outline-success btn-sm" onClick={exportCsv}>Export To Excel</button></>}</div>
      </div></div>
    </div>

    {report && <section className="legacy-report mt-3">
      <header className="legacy-report-header"><h1>{company.CompanyName || "Global Metals"}</h1><h2>{report.report.label}</h2></header>
      <div className="report-meta"><span><b>Category:</b> {report.report.category}</span><span><b>Total Records:</b> {report.totalRecords}</span><span><b>Generated:</b> {formatReportDate(report.criteria.generatedAt)}</span></div>
      {(report.sections || [report]).map(section => <div key={section.status || "report"} className="report-group">
      {section.label && <h3 className="fs-6">{section.label} ({section.rows.length})</h3>}
      <div className="table-responsive"><table className="legacy-report-table"><thead><tr><th>#</th>{report.columns.map(column => <th key={column.key}>{column.label}</th>)}</tr></thead>
        <tbody>{!section.rows.length ? <tr><td colSpan={report.columns.length + 1} className="report-empty">No records found.</td></tr> : section.rows.map((row, index) => <tr key={index}><td>{index + 1}</td>{report.columns.map(column => <td key={column.key}>{formatValue(row[column.key])}</td>)}</tr>)}</tbody>
      </table></div></div>)}
    </section>}
  </div>;
};

export default AdditionalReports;
